import { useState, useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import { collection, onSnapshot, query, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../hooks/useAuth';
import L from 'leaflet';
import './ApprovedImagesPanel.css';

interface ApprovedImage {
  id: string;
  file_path: string;
  image_url: string;
  metadata: {
    filename: string;
    size?: number;
    width?: number;
    height?: number;
    dji?: {
      drone_location?: {
        latitude: number;
        longitude: number;
        absolute_altitude?: number;
      };
      lrf_location?: {
        latitude: number;
        longitude: number;
        altitude?: number;
        distance_from_drone?: number;
      };
      drone_model?: string;
    };
    gps?: {
      latitude: number;
      longitude: number;
      altitude?: number;
    };
  };
  approved_at: any;
  approved_by: string;
}

interface ApprovedImagesPanelProps {
  visible: boolean;
}

const ApprovedImagesPanel = ({ visible }: ApprovedImagesPanelProps) => {
  const map = useMap();
  const { user, isAdmin } = useAuth();
  const panelRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(true);
  const [approvedImages, setApprovedImages] = useState<ApprovedImage[]>([]);
  const [imageMarkers, setImageMarkers] = useState<L.CircleMarker[]>([]);
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  const [modalImage, setModalImage] = useState<ApprovedImage | null>(null);
  const [deletingImageId, setDeletingImageId] = useState<string | null>(null);

  // Prevent map zoom when scrolling over the panel
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    const handleWheel = (e: WheelEvent) => {
      // Stop event from reaching the map
      e.stopPropagation();
    };

    panel.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      panel.removeEventListener('wheel', handleWheel);
    };
  }, []);

  // Fetch approved images from Firestore
  useEffect(() => {
    if (!visible || !user) return;

    const q = query(
      collection(db, 'approved_images'),
      orderBy('approved_at', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const images: ApprovedImage[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        console.log('Approved image from Firestore:', doc.id, data);
        images.push({
          id: doc.id,
          ...data,
        } as ApprovedImage);
      });
      console.log('Total approved images:', images.length);
      setApprovedImages(images);
    });

    return () => unsubscribe();
  }, [visible, user]);

  // Create circle markers for approved images
  useEffect(() => {
    // Clear existing markers
    imageMarkers.forEach(marker => map.removeLayer(marker));

    if (!visible) {
      setImageMarkers([]);
      return;
    }

    const newMarkers: L.CircleMarker[] = [];

    approvedImages.forEach((image) => {
      let lat: number | null = null;
      let lon: number | null = null;
      let label = '';

      // Prioritize DJI LRF location (if valid), then drone location, then standard GPS
      if (image.metadata.dji?.lrf_location) {
        const lrfLat = image.metadata.dji.lrf_location.latitude;
        const lrfLon = image.metadata.dji.lrf_location.longitude;
        // Check if LRF coordinates are valid (not 0,0)
        if (lrfLat !== 0 && lrfLon !== 0) {
          lat = lrfLat;
          lon = lrfLon;
          label = 'LRF Target';
        } else if (image.metadata.dji?.drone_location) {
          lat = image.metadata.dji.drone_location.latitude;
          lon = image.metadata.dji.drone_location.longitude;
          label = 'Drone Location';
        }
      } else if (image.metadata.dji?.drone_location) {
        lat = image.metadata.dji.drone_location.latitude;
        lon = image.metadata.dji.drone_location.longitude;
        label = 'Drone Location';
      } else if (image.metadata.gps) {
        lat = image.metadata.gps.latitude;
        lon = image.metadata.gps.longitude;
        label = 'GPS Location';
      }

      if (lat !== null && lon !== null) {
        const marker = L.circleMarker([lat, lon], {
          radius: 8,
          fillColor: image.id === selectedImageId ? '#3b82f6' : '#10b981',
          color: '#fff',
          weight: 2,
          opacity: 1,
          fillOpacity: 0.8,
        });

        const filename = image.metadata.filename || 'Unknown';
        let approvedDate = 'Unknown';
        if (image.approved_at) {
          try {
            if (image.approved_at instanceof Timestamp) {
              approvedDate = image.approved_at.toDate().toLocaleString();
            } else if (image.approved_at.toDate) {
              approvedDate = image.approved_at.toDate().toLocaleString();
            }
          } catch (e) {
            console.error('Error parsing date:', e);
          }
        }

        marker.bindPopup(`
          <div style="min-width: 200px;">
            <div style="font-weight: 600; margin-bottom: 8px;">${filename}</div>
            <div style="font-size: 12px; color: #666; margin-bottom: 8px;">${label}</div>
            <div style="font-size: 11px; color: #999;">Approved: ${approvedDate}</div>
            <div style="font-size: 11px; color: #999;">By: ${image.approved_by}</div>
          </div>
        `);

        marker.on('click', () => {
          setSelectedImageId(image.id);
          setModalImage(image);
        });

        marker.addTo(map);
        newMarkers.push(marker);
      }
    });

    setImageMarkers(newMarkers);

    return () => {
      newMarkers.forEach(marker => map.removeLayer(marker));
    };
  }, [approvedImages, visible, map, selectedImageId]);

  const zoomToImage = (image: ApprovedImage) => {
    let lat: number | null = null;
    let lon: number | null = null;

    // Prioritize DJI LRF location (if valid), then drone location, then standard GPS
    if (image.metadata.dji?.lrf_location) {
      const lrfLat = image.metadata.dji.lrf_location.latitude;
      const lrfLon = image.metadata.dji.lrf_location.longitude;
      // Check if LRF coordinates are valid (not 0,0)
      if (lrfLat !== 0 && lrfLon !== 0) {
        lat = lrfLat;
        lon = lrfLon;
      } else if (image.metadata.dji?.drone_location) {
        lat = image.metadata.dji.drone_location.latitude;
        lon = image.metadata.dji.drone_location.longitude;
      }
    } else if (image.metadata.dji?.drone_location) {
      lat = image.metadata.dji.drone_location.latitude;
      lon = image.metadata.dji.drone_location.longitude;
    } else if (image.metadata.gps) {
      lat = image.metadata.gps.latitude;
      lon = image.metadata.gps.longitude;
    }

    if (lat !== null && lon !== null) {
      map.setView([lat, lon], 18, { animate: true });
      setSelectedImageId(image.id);
    }
  };

  const handleDeleteImage = async (imageId: string) => {
    if (!isAdmin) {
      alert('Only administrators can delete images');
      return;
    }

    if (!confirm('Are you sure you want to delete this image? This action cannot be undone.')) {
      return;
    }

    setDeletingImageId(imageId);

    try {
      // Call the Cloud Function to delete the image
      const cloudFunctionUrl = 'https://europe-north1-upload-and-processing.cloudfunctions.net/delete_approved_image';

      const response = await fetch(cloudFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ image_id: imageId }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete image');
      }

      console.log('Image deleted successfully');
      // The real-time listener will automatically update the UI
    } catch (error) {
      console.error('Error deleting image:', error);
      alert(`Failed to delete image: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setDeletingImageId(null);
    }
  };

  if (!visible) return null;

  return (
    <>
      <div ref={panelRef} className={`approved-images-panel ${isOpen ? 'open' : 'closed'}`}>
        <div className="panel-header">
          <h3>Approved Images ({approvedImages.length})</h3>
          <button
            className="toggle-button"
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? 'Close panel' : 'Open panel'}
          >
            {isOpen ? '→' : '←'}
          </button>
        </div>

        {isOpen && (
          <div className="panel-content">
            {!user ? (
              <div className="no-images">
                <p>Please log in to view approved images</p>
              </div>
            ) : approvedImages.length === 0 ? (
              <div className="no-images">
                <p>No approved images yet</p>
              </div>
            ) : (
              <div className="images-list">
                {approvedImages.map((image) => {
                  const hasLocation =
                    image.metadata.dji?.lrf_location ||
                    image.metadata.dji?.drone_location ||
                    image.metadata.gps;

                  const locationInfo = image.metadata.dji?.lrf_location
                    ? 'LRF Target'
                    : image.metadata.dji?.drone_location
                    ? 'Drone'
                    : 'GPS';

                  return (
                    <div
                      key={image.id}
                      className={`image-item ${selectedImageId === image.id ? 'selected' : ''}`}
                    >
                      {image.image_url && (
                        <img
                          src={image.image_url}
                          alt={image.metadata.filename || 'Approved image'}
                          className="image-thumbnail"
                          loading="lazy"
                          onClick={() => setModalImage(image)}
                          style={{ cursor: 'pointer' }}
                        />
                      )}
                      <div className="image-info">
                        <div className="image-filename">
                          {image.metadata.filename || 'Unknown'}
                        </div>
                        <div className="image-meta">
                          {image.metadata.width && image.metadata.height && (
                            <span>
                              {image.metadata.width}x{image.metadata.height}
                            </span>
                          )}
                          {hasLocation && (
                            <span className="location-badge">{locationInfo}</span>
                          )}
                        </div>
                        <div className="image-approved">
                          Approved by {image.approved_by}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                        {hasLocation && (
                          <button
                            className="zoom-button"
                            onClick={() => zoomToImage(image)}
                            title="Zoom to location"
                          >
                            🎯
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            className="delete-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteImage(image.id);
                            }}
                            disabled={deletingImageId === image.id}
                            title="Delete image (Admin only)"
                          >
                            {deletingImageId === image.id ? '⏳' : '🗑️'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Image Modal */}
      {modalImage && (
        <div className="image-modal-overlay" onClick={() => setModalImage(null)}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setModalImage(null)}>
              ✕
            </button>
            <img
              src={modalImage.image_url}
              alt={modalImage.metadata.filename || 'Approved image'}
              className="modal-image"
            />
            <div className="modal-info">
              <h3>{modalImage.metadata.filename || 'Unknown'}</h3>
              <div className="modal-metadata">
                {modalImage.metadata.width && modalImage.metadata.height && (
                  <p>
                    <strong>Dimensions:</strong> {modalImage.metadata.width} x{' '}
                    {modalImage.metadata.height}
                  </p>
                )}
                {modalImage.metadata.dji?.drone_model && (
                  <p>
                    <strong>Drone:</strong> {modalImage.metadata.dji.drone_model}
                  </p>
                )}
                {modalImage.metadata.dji?.lrf_location && (
                  <p>
                    <strong>LRF Target:</strong>{' '}
                    {modalImage.metadata.dji.lrf_location.latitude.toFixed(6)},{' '}
                    {modalImage.metadata.dji.lrf_location.longitude.toFixed(6)}
                    {modalImage.metadata.dji.lrf_location.distance_from_drone && (
                      <span> ({modalImage.metadata.dji.lrf_location.distance_from_drone}m from drone)</span>
                    )}
                  </p>
                )}
                {modalImage.metadata.dji?.drone_location && (
                  <p>
                    <strong>Drone Location:</strong>{' '}
                    {modalImage.metadata.dji.drone_location.latitude.toFixed(6)},{' '}
                    {modalImage.metadata.dji.drone_location.longitude.toFixed(6)}
                    {modalImage.metadata.dji.drone_location.absolute_altitude && (
                      <span> (Alt: {modalImage.metadata.dji.drone_location.absolute_altitude}m)</span>
                    )}
                  </p>
                )}
                <p>
                  <strong>Approved by:</strong> {modalImage.approved_by}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ApprovedImagesPanel;
